import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FileRow } from '@/components/ged/FileRow'
import { FileDrawer } from '@/components/ged/FileDrawer'
import { getAreaTree, areaKeys } from '@/lib/api/areas'
import { listDocuments, documentKeys } from '@/lib/api/documents'
import type { AreaNode, ListDocumentsParams } from '@/lib/types/ged'

type LeafOption = { id: string; name: string; path: string }

function collectLeaves(nodes: AreaNode[], ancestors: string[] = []): LeafOption[] {
  const out: LeafOption[] = []
  for (const n of nodes) {
    if (n.children.length === 0) {
      out.push({ id: n.id, name: n.name, path: ancestors.join(' › ') })
    } else {
      out.push(...collectLeaves(n.children, [...ancestors, n.name]))
    }
  }
  return out
}

const PAGE_SIZE = 20

export function SearchPage() {
  const [search, setSearch] = useState('')
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [submittedSearch, setSubmittedSearch] = useState('')

  const { data: tree = [] } = useQuery({
    queryKey: areaKeys.tree(),
    queryFn: getAreaTree,
  })

  const leafAreas = useMemo(() => collectLeaves(tree), [tree])

  const params: ListDocumentsParams = {
    search: submittedSearch || undefined,
    areaId: selectedAreaId || undefined,
    page,
    pageSize: PAGE_SIZE,
  }

  const { data, isLoading } = useQuery({
    queryKey: documentKeys.list(params),
    queryFn: () => listDocuments(params),
    enabled: !!(submittedSearch || selectedAreaId),
    placeholderData: (prev) => prev,
  })

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const hasQuery = !!(submittedSearch || selectedAreaId)

  function handleSearch() {
    setSubmittedSearch(search)
    setPage(1)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleSearch()
  }

  function clearAll() {
    setSearch('')
    setSubmittedSearch('')
    setSelectedAreaId(null)
    setPage(1)
  }

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={{ flex: 1, padding: '24px 32px', overflow: 'auto', minWidth: 0 }}>
        {/* Breadcrumb */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            color: 'var(--ev-fg-muted)',
            marginBottom: 16,
          }}
        >
          <Link to="/" style={{ color: 'var(--ev-fg-muted)', textDecoration: 'none' }}>
            Início
          </Link>
          <span style={{ color: 'var(--ev-fg-faint)', fontSize: 11 }}>›</span>
          <span style={{ color: 'var(--ev-fg)', fontWeight: 500 }}>Buscar</span>
        </div>

        {/* Search input */}
        <div style={{ position: 'relative', marginBottom: 18 }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: 14,
              top: 15,
              color: 'var(--ev-accent)',
              pointerEvents: 'none',
            }}
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar documentos por título…"
            style={{
              height: 48,
              fontSize: 15,
              paddingLeft: 44,
              paddingRight: 100,
              borderColor: 'var(--ev-accent)',
              boxShadow: 'var(--ev-shadow-focus)',
            }}
          />
          <div style={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 4 }}>
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background: 'var(--ev-bg-alt)',
                  border: 0,
                  borderRadius: 6,
                  padding: '6px 10px',
                  fontSize: 12,
                  color: 'var(--ev-fg-muted)',
                  cursor: 'pointer',
                }}
              >
                <X size={12} />
              </button>
            )}
            <Button
              onClick={handleSearch}
              style={{
                background: 'var(--ev-accent)',
                color: 'white',
                border: 0,
                height: 32,
                fontSize: 13,
              }}
            >
              Buscar
            </Button>
          </div>
        </div>

        {/* Two-column layout: filters + results */}
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24 }}>
          {/* Filters sidebar */}
          <aside>
            {/* Area filter */}
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--ev-fg-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 10,
              }}
            >
              Área
            </div>
            {leafAreas.map((area) => (
              <label
                key={area.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '5px 0',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
                title={area.path ? `${area.path} › ${area.name}` : area.name}
              >
                <input
                  type="checkbox"
                  checked={selectedAreaId === area.id}
                  onChange={(e) => {
                    setSelectedAreaId(e.target.checked ? area.id : null)
                    setPage(1)
                  }}
                  style={{ accentColor: 'var(--ev-accent)', marginTop: 3 }}
                />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block' }}>{area.name}</span>
                  {area.path && (
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--ev-fg-muted)', marginTop: 1 }}>
                      {area.path}
                    </span>
                  )}
                </span>
              </label>
            ))}

            {hasQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                style={{ marginTop: 16, color: 'var(--ev-fg-muted)', width: '100%' }}
              >
                Limpar filtros
              </Button>
            )}
          </aside>

          {/* Results */}
          <div>
            {hasQuery && !isLoading && (
              <div style={{ fontSize: 13, color: 'var(--ev-fg-muted)', marginBottom: 14 }}>
                <strong style={{ color: 'var(--ev-fg)' }}>{totalCount}</strong>{' '}
                {totalCount === 1 ? 'resultado' : 'resultados'}
                {submittedSearch && (
                  <>
                    {' '}para{' '}
                    <em style={{ color: 'var(--ev-fg)' }}>"{submittedSearch}"</em>
                  </>
                )}
              </div>
            )}

            {!hasQuery && (
              <div style={{ color: 'var(--ev-fg-muted)', fontSize: 14, paddingTop: 40, textAlign: 'center' }}>
                <Search size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p>Digite algo para buscar documentos.</p>
              </div>
            )}

            {isLoading && (
              <p style={{ color: 'var(--ev-fg-muted)', fontSize: 13 }}>Buscando…</p>
            )}

            {hasQuery && !isLoading && items.length === 0 && (
              <p style={{ color: 'var(--ev-fg-muted)', fontSize: 13 }}>Nenhum resultado encontrado.</p>
            )}

            {items.length > 0 && (
              <>
                <div
                  style={{
                    background: 'var(--ev-bg-elevated)',
                    border: '1px solid var(--ev-border)',
                    borderRadius: 10,
                    overflow: 'hidden',
                    marginBottom: 14,
                  }}
                >
                  {items.map((doc) => (
                    <FileRow
                      key={doc.id}
                      doc={doc}
                      showArea
                      onClick={setSelectedDocId}
                      isSelected={doc.id === selectedDocId}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--ev-fg-muted)' }}>
                  <span>
                    Mostrando {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min(page * PAGE_SIZE, totalCount)} de {totalCount}
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((n) => (
                      <Button
                        key={n}
                        variant={n === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(n)}
                        style={{
                          minWidth: 30,
                          justifyContent: 'center',
                          padding: 0,
                          background: n === page ? 'var(--ev-accent)' : undefined,
                          borderColor: n === page ? 'var(--ev-accent)' : undefined,
                        }}
                      >
                        {n}
                      </Button>
                    ))}
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File drawer */}
      <FileDrawer documentId={selectedDocId} onClose={() => setSelectedDocId(null)} />
    </div>
  )
}
